import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import apiClient from '../../../api/client';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '../../../components/ui/dialog';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot
} from '../../../components/ui/input-otp';

enum RemovePinStep {
  EnterPin,
  Success
}

interface IProps {
  onSuccess: () => void;
}

const RemovePin: React.FC<IProps> = ({ onSuccess }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [pin, setPin] = useState('');
  const [step, setStep] = useState(RemovePinStep.EnterPin);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setPin('');
    setStep(RemovePinStep.EnterPin);
    removePin.reset();
  };

  const removePin = useMutation({
    mutationFn: async ({ pin }: { pin: string }) => {
      await apiClient.delete('/users/pin', { data: { pin } });
    },
    onSuccess: () => {
      setStep(RemovePinStep.Success);
      onSuccess();
    },
    onError: () => {
      setPin('');
    }
  });

  const handlePinChange = (value: string) => {
    setPin(value);
    removePin.reset();
    if (value.length === 6) {
      removePin.mutate({ pin: value });
    }
  };

  return (
    <>
      <div className="flex flex-col gap-2 border-t-1 py-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-medium text-gray-900">Remove PIN</h3>
            <p className="text-sm text-gray-500">
              This will also disable your payment limit.
            </p>
          </div>
          <Button
            type="button"
            className="min-w-24"
            onClick={() => setOpenDialog(true)}
          >
            Remove
          </Button>
        </div>
        <p className="rounded-lg bg-orange-50 px-3 py-2 text-sm text-orange-600">
          Removing your PIN will reset your transaction limit. All transactions
          will proceed without PIN verification.
        </p>
      </div>

      <Dialog open={openDialog} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-[24rem] rounded-3xl">
          {step === RemovePinStep.EnterPin && (
            <DialogHeader className="mt-5">
              <DialogTitle className="text-center">
                Enter Your 6-Digit PIN
              </DialogTitle>
            </DialogHeader>
          )}

          {step === RemovePinStep.EnterPin ? (
            <>
              <p className="text-center text-sm text-foreground/90">
                Enter your current PIN to confirm removal
              </p>

              <form className="mb-10 flex flex-col items-center">
                <InputOTP
                  maxLength={6}
                  value={pin}
                  onChange={handlePinChange}
                  disabled={removePin.isPending}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                  </InputOTPGroup>
                  <InputOTPGroup>
                    <InputOTPSlot index={1} />
                  </InputOTPGroup>
                  <InputOTPGroup>
                    <InputOTPSlot index={2} />
                  </InputOTPGroup>
                  <InputOTPGroup>
                    <InputOTPSlot index={3} />
                  </InputOTPGroup>
                  <InputOTPGroup>
                    <InputOTPSlot index={4} />
                  </InputOTPGroup>
                  <InputOTPGroup>
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>

                {removePin.isError && (
                  <span className="mt-2 text-xs text-red-500">
                    {(removePin.error as any)?.response?.data?.error?.message ||
                      'Incorrect PIN. Please try again.'}
                  </span>
                )}
              </form>

              <Button
                variant="link"
                className="decoration-[#5B5959]"
                onClick={handleCloseDialog}
              >
                <p className="text-[#5B5959]">Cancel</p>
              </Button>
            </>
          ) : (
            <div className="mb-1 mt-3 flex flex-col items-center">
              <img
                src="/assets/images/update-phone-success.png"
                alt="PIN Removed"
                className="mx-auto w-32"
              />

              <h3 className="mt-4 text-center text-sm font-medium">
                Your PIN has been removed successfully
              </h3>

              <Button
                className="mx-4 mt-6 w-4/5 rounded-full bg-[#5CB891] hover:bg-[#5CB891]"
                onClick={handleCloseDialog}
                type="button"
              >
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RemovePin;
